import React from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  Typography,
  Box,
} from "@mui/material";
import { useDeletePub, Pub as PubType } from "../../../api/pub";
import { CustomLoader } from "../../../components/CustomLoader/CustomLoader";
import useStyles from "./styles";

interface ModalDeletePubProps {
  open: boolean;
  pubInitial: PubType;
  onClose: () => void;
  onCancel: () => void;
}

export const ModalDeletePub: React.FC<ModalDeletePubProps> = ({
  open,
  pubInitial,
  onClose,
  onCancel,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const deletePubMutation = useDeletePub();

  const handleDelete = () => {
    if (pubInitial.id !== -1) {
      deletePubMutation.mutate(pubInitial.id, {
        onSuccess: () => {
          onClose();
        },
      });
    }
  };

  return (
    <>
      {deletePubMutation.isPending && (
        <CustomLoader isActive={deletePubMutation.isPending} />
      )}
      
      <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
        <DialogTitle>
          {t("pub.deletePubTitle", "Delete Pub")}
        </DialogTitle>
        
        <DialogContent>
          <DialogContentText>
            {t(
              "pub.deletePubConfirmation",
              "Are you sure you want to delete this pub? This action cannot be undone."
            )}
          </DialogContentText>

          {pubInitial.name && (
            <Box className={classes.infoBox}>
              <Typography variant="body2" color="text.secondary">
                {t("pub.pubName", "Pub Name")}:
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {pubInitial.name}
              </Typography>
              
              {pubInitial.city && (
                <>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    className={classes.infoLabel}
                  >
                    {t("pub.city", "City")}:
                  </Typography>
                  <Typography variant="body1">
                    {pubInitial.city}
                  </Typography>
                </>
              )}

              {pubInitial.sign_id && (
                <>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    className={classes.infoLabel}
                  >
                    {t("pub.assignedToSign", "Assigned to Sign")}:
                  </Typography>
                  <Typography variant="body1">
                    Yes
                  </Typography>
                </>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onCancel} disabled={deletePubMutation.isPending}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={deletePubMutation.isPending}
          >
            {t("common.delete", "Delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};